import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { getUser } from '../../utils/auth';
import DuplicatePt5Modal from '../../components/DuplicatePt5Modal';
import { useDuplicatePt5Retry } from '../../hooks/useDuplicatePt5Retry';
import { trackAction } from '../../utils/clickTracking';

const toFiniteNumber = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === "number") return Number.isFinite(val) ? val : null;
  const n = Number(String(val).replaceAll(",", "").trim());
  return Number.isFinite(n) ? n : null;
};

const formatTwoDecimals = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value).trim();
  if (!str) return "";
  const lowered = str.toLowerCase();
  if (lowered === "null" || lowered === "undefined") return "";
  const n = toFiniteNumber(str);
  if (n === null) return str;
  return String(Math.round(n));
};

const hasUsableValue = (value) => {
  if (value === null || value === undefined) return false;
  const str = String(value).trim().toLowerCase();
  return str !== "" && str !== "null" && str !== "undefined" && str !== "n/a";
};

const pickFirstValue = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (hasUsableValue(value)) return value;
  }
  return "";
};

const getMLDisplayValue = (property, field) => {
  let realValue = "";

  switch (field) {
    case 'floor':
      realValue = pickFirstValue(property, ["floors_ml", "floor_ml", "floors_pred", "floor_pred"]);
      break;
    case 'landuse':
      realValue = pickFirstValue(property, ["landuse_ml", "land_use_ml", "land_use_pred", "landuse_pred"]);
      break;
    case 'landarea':
      {
        const ml = pickFirstValue(property, ["landarea_ml", "land_area_ml"]);
        const bucket = pickFirstValue(property, ["landarea_combined_pred"]);
        let n;
        try {
          n = Number(String(ml ?? "").replaceAll(",", "").trim());
        } catch (e) {
          n = NaN;
        }
        const mlIsZeroish = Number.isFinite(n) && n <= 0;
        realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
      }
      break;
    case 'coveredarea':
      {
        const ml = pickFirstValue(property, ["coveredarea_ml", "covered_area_ml"]);
        const bucket = pickFirstValue(property, ["coveredarea_combined_pred"]);
        let n;
        try {
          n = Number(String(ml ?? "").replaceAll(",", "").trim());
        } catch (e) {
          n = NaN;
        }
        const mlIsZeroish = Number.isFinite(n) && n <= 0;
        realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
      }
      break;
  }

  if (hasUsableValue(realValue)) {
    if (field === "landarea" || field === "coveredarea") {
      const strVal = String(realValue ?? "");
      const isRange = strVal.toLowerCase().includes("greater") ||
        strVal.toLowerCase().includes("less") ||
        strVal.toLowerCase().includes("between");
      if (isRange) {
        return realValue;
      }
      const formatted = formatTwoDecimals(realValue);
      return formatted || "N/A";
    }
    const num = Number(realValue);
    if (Number.isFinite(num) && !isNaN(num)) {
      return Number.isInteger(num) ? num : Math.round(num);
    }
    return realValue;
  }
  return 'N/A';
};

const parseRange = (rangeStr) => {
  if (!rangeStr) return null;
  const str = String(rangeStr).toLowerCase().trim();
  const cleanStr = str.replace(/sq\.?\s*(yards?|yds?|ft|feet?)|,/g, '').trim();

  let match = cleanStr.match(/(?:greater|more)\s+than\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return { type: 'greater', value: Number(match[1]) };
  }

  match = cleanStr.match(/(?:less|under)\s+than\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return { type: 'less', value: Number(match[1]) };
  }

  match = cleanStr.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/);
  if (match) {
    return {
      type: 'between',
      min: Number(match[1]),
      max: Number(match[2])
    };
  }

  const num = Number(cleanStr);
  if (Number.isFinite(num)) {
    return { type: 'exact', value: num };
  }

  return null;
};

const areComparableValuesDifferent = (adminVal, mlVal, field, selectedProperty) => {
  const normalize = (val) => {
    if (val === null || val === undefined) return null;
    const raw = String(val).trim();
    if (!raw) return null;
    const lowered = raw.toLowerCase();
    if (lowered === "n/a" || lowered === "null" || lowered === "undefined") return null;
    if (field === "landarea" || field === "coveredarea") {
      const numStr = raw.replace(/[^0-9.]/g, '');
      const numeric = Number(numStr);
      if (Number.isFinite(numeric)) return numeric;
    }
    const numeric = Number(raw.replaceAll(",", ""));
    if (Number.isFinite(numeric)) return numeric;
    return lowered;
  };

  const a = normalize(adminVal);

  if ((field === "landarea" || field === "coveredarea") && mlVal) {
    const range = parseRange(mlVal);
    if (range && typeof a === "number") {
      switch (range.type) {
        case "greater":
          return a <= range.value;
        case "less":
          return a >= range.value;
        case "between":
          return a < range.min || a > range.max;
        case "exact":
          return Math.abs(a - range.value) > 1e-9;
      }
    }
  }

  const m = normalize(getMLDisplayValue(selectedProperty, field));
  if (a === null || m === null) return false;
  if (typeof a === "number" && typeof m === "number") return Math.abs(a - m) > 1e-9;
  return a !== m;
};

const getPt5Reference = (property) =>
  property?.refno ?? property?.pt5 ?? property?.pt_5 ?? property?.pt ?? "";

/** True only when the user entered an Updated value that differs from Admin. */
const isAfterValueChanged = (feature) => {
  if (!hasUsableValue(feature?.after)) return false;

  if (["floor", "landarea", "coveredarea"].includes(feature.key)) {
    const adminNum = toFiniteNumber(feature.admin);
    const afterNum = toFiniteNumber(feature.after);
    if (adminNum !== null && afterNum !== null) {
      return Math.abs(adminNum - afterNum) > 1e-9;
    }
  }

  return (
    String(feature.after).trim().toLowerCase() !==
    String(feature.admin ?? "").trim().toLowerCase()
  );
};

const ReassessmentFormPage = () => {
  const { pin } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const agreeWithAiFromNav = location.state?.agreeWithAi;
  const user = getUser();
  const userId = user?.id;

  console.log("ReassessmentFormPage rendered. PIN:", pin, "UserId:", userId, "agreeWithAiFromNav:", agreeWithAiFromNav);

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  const [overallExplanation, setOverallExplanation] = useState("");
  const [rowExplanations, setRowExplanations] = useState({
    floor: "",
    landuse: "",
    landarea: "",
    coveredarea: ""
  });
  const [features, setFeatures] = useState([]);
  const [reassessSubmitting, setReassessSubmitting] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [pt6IssueDate, setPt6IssueDate] = useState("");
  const [showRefModal, setShowRefModal] = useState(false);
  const {
    showDuplicatePt5Modal,
    openDuplicatePt5Modal,
    closeDuplicatePt5Modal,
    saveDuplicatePt5Retry,
  } = useDuplicatePt5Retry();

  const applyNewPt5 = (newPt5) => {
    setReferenceNumber(newPt5);
  };

  const isCurrentlyReassessed =
    selectedProperty?.reassess === 1 || selectedProperty?.reassess === "1";

  // Same validity rules as pending-review (assignedai.jsx)
  const hasReassessChanges = features.some((f) => isAfterValueChanged(f));
  const hasReassessExplanation = overallExplanation.trim() !== "";
  const isReassessValid = hasReassessChanges && hasReassessExplanation;

  const buildFeatures = (p) => {
    if (!p) return [];

    const hasSeparateInspectorAssessment = Number(p.inspector_assessment_version) === 1;
    const featureKeys = ["floor", "landuse", "landarea", "coveredarea"];
    return featureKeys.map((key) => {
      const adminValue = p[`${key}_admin`];
      const mlValue = getMLDisplayValue(p, key);
      const inspectorField = {
        floor: "inspector_floor",
        landuse: "inspector_landuse",
        landarea: "inspector_landarea",
        coveredarea: "inspector_coveredarea",
      }[key];
      const afterValue = hasSeparateInspectorAssessment
        ? p[inspectorField] ?? ""
        : p[`${key}_after`] !== undefined && p[`${key}_after`] !== null
          ? p[`${key}_after`]
          : "";

      return {
        key: key,
        label:
          key.charAt(0).toUpperCase() +
          key.slice(1).replace(/area/, " Area").replace(/use/, " Use"),
        admin: adminValue,
        ml: mlValue,
        after: afterValue,
        originalAfter: afterValue,
        explanation: rowExplanations[key] || p[`${key}_explanation`] || "",
        isDifferent: areComparableValuesDifferent(adminValue, mlValue, key, p),
      };
    });
  };

  const updateFeature = (index, field, value) => {
    setFeatures((prev) => {
      const updated = [...prev];
      const f = updated[index];

      let validatedValue = value;
      if (field === "after") {
        const oldValStr = String(f.admin || "").trim().toLowerCase();
        const newValStr = String(validatedValue || "").trim().toLowerCase();
        if (oldValStr !== "" && newValStr !== "" && oldValStr === newValStr) {
          toast.error("New value must be different from current value!");
          return prev;
        }

        switch (f.key) {
          case "floor":
          case "landarea":
          case "coveredarea":
            if (/[^0-9.]/.test(value)) {
              toast.error("Please enter only numbers for this field!");
              validatedValue = value.replace(/[^0-9.]/g, "");
            }
            if ((validatedValue.match(/\./g) || []).length > 1) {
              validatedValue = validatedValue.slice(0, -1);
            }
            if (validatedValue) {
              const numVal = parseFloat(validatedValue);
              if (f.key === "landarea") {
                if (numVal < 0 || numVal > 108000) {
                  toast.error("Land Area must be between 0 and 108,000 sq yards!");
                }
              } else if (f.key === "coveredarea") {
                if (numVal < 0 || numVal > 272000) {
                  toast.error("Covered Area must be between 0 and 272,000 sq ft!");
                }
              }
            }
            break;
          case "landuse":
            if (/[0-9]/.test(value)) {
              toast.error("Please enter only letters for Land Use!");
              validatedValue = value.replace(/[0-9]/g, "");
            }
            break;
        }
      }

      updated[index] = { ...f, [field]: validatedValue };
      return updated;
    });
  };

  const updateRowExplanation = (key, value) => {
    setRowExplanations((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const validateReassessForm = () => {
    // Same messages/order as pending-review
    if (!hasReassessExplanation) {
      toast.error("Please enter explanation of reassessment");
      return false;
    }
    if (!hasReassessChanges) {
      toast.error("Please update at least one feature");
      return false;
    }

    for (const feature of features) {
      if (["floor", "landarea", "coveredarea"].includes(feature.key)) {
        if (
          hasUsableValue(feature.after) &&
          toFiniteNumber(feature.after) === null
        ) {
          toast.error(`${feature.label} must be a valid number.`);
          return false;
        }
      }
      if (feature.key === "landarea" && hasUsableValue(feature.after)) {
        const numVal = parseFloat(feature.after);
        if (numVal < 0 || numVal > 108000) {
          toast.error("Land Area must be between 0 and 108,000 sq yards!");
          return false;
        }
      }
      if (feature.key === "coveredarea" && hasUsableValue(feature.after)) {
        const numVal = parseFloat(feature.after);
        if (numVal < 0 || numVal > 272000) {
          toast.error("Covered Area must be between 0 and 272,000 sq ft!");
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmitReassess = async ({ featuresOverride, refnoOverride, pt6IssueDateOverride } = {}) => {
    const activeFeatures = featuresOverride || features;
    const activeRefno = refnoOverride || referenceNumber;
    const activePt6Date = pt6IssueDateOverride ?? pt6IssueDate;
    if (!selectedProperty || reassessSubmitting) return;

    if (!validateReassessForm()) return;

    // if (!String(activeRefno || "").trim()) {
    //   toast.error("PT-5 Serial Number is mandatory for reassessment.");
    //   return;
    // }

    setReassessSubmitting(true);
    const toastId = toast.loading("Submitting reassessment...");

    try {
      // 1. Save algorithm flag response (agreeWithAi) FIRST, if user selected an option, with empty comment
      if (agreeWithAiFromNav !== undefined && agreeWithAiFromNav !== null) {
        const agreeValue = agreeWithAiFromNav === 'partial' ? 2 : (agreeWithAiFromNav ? 1 : 0);
        await axios.post('/inspector/algorithm-flag-response', {
          propertyId: selectedProperty.property_id || selectedProperty.id,
          inspectorId: user.id || user.userId,
          response: "",
          agreeWithAi: agreeValue,
        });
      }

      // 2. Save explanation if present
      if (overallExplanation || Object.values(rowExplanations).some(hasUsableValue)) {
        await axios.post("/inspector/save-explanation", {
          property_id: selectedProperty.property_id || selectedProperty.id,
          user_id: user.id || user.userId,
          overall_explanation: overallExplanation,
          row_explanations: rowExplanations,
        });
      }

      // 3. Update property with reassessment values
      const formData = new FormData();
      formData.append("pin", selectedProperty.pin);
      formData.append("userId", user.id || user.userId);
      formData.append("reassess", "1"); // Mark as reassessed -> goes to Resolved tab
      formData.append("status", "resolved"); // Required for supervisor flow compatibility
      formData.append("propertyId", selectedProperty.property_row_id || selectedProperty.property_id);

      // Required fields the backend UPDATE statement needs — without these,
      // updateProperty() binds `undefined` and nulls out these columns.
      formData.append("circle", selectedProperty.circle || "");
      formData.append("occupationstatus", selectedProperty.occupationstatus || "");
      formData.append("location", selectedProperty.location || "");
      formData.append("admin_garv", selectedProperty.admin_garv || "");
      formData.append("exp_garv", selectedProperty.exp_garv || "");
      formData.append("floor_admin", selectedProperty.floor_admin || "");
      formData.append("landuse_admin", selectedProperty.landuse_admin || "");
      formData.append("landarea_admin", selectedProperty.landarea_admin || "");
      formData.append("coveredarea_admin", selectedProperty.coveredarea_admin || "");
      formData.append("floors_ml", selectedProperty.floors_ml || "");
      formData.append("landuse_ml", selectedProperty.landuse_ml || "");
      formData.append("landarea_ml", selectedProperty.landarea_ml || "");
      formData.append("coveredarea_ml", selectedProperty.coveredarea_ml || "");

      // Keep model predictions separate from values entered by the inspector.
      const preds = {
        floors_pred: selectedProperty.floors_pred || "",
        land_use_pred: selectedProperty.land_use_pred || "",
        landarea_combined_pred: selectedProperty.landarea_combined_pred || "",
        coveredarea_combined_pred: selectedProperty.coveredarea_combined_pred || "",
      };
      const inspectorAssessment = {
        inspector_floor: "",
        inspector_landuse: "",
        inspector_landarea: "",
        inspector_coveredarea: "",
      };

      activeFeatures.forEach((feature) => {
        const val = feature.after?.toString().trim();
        if (!val) return;
        switch (feature.key) {
          case "floor":
            inspectorAssessment.inspector_floor = val;
            break;
          case "landuse":
            inspectorAssessment.inspector_landuse = val;
            break;
          case "landarea":
            inspectorAssessment.inspector_landarea = val;
            break;
          case "coveredarea":
            inspectorAssessment.inspector_coveredarea = val;
            break;
        }
      });

      Object.entries(preds).forEach(([key, val]) => {
        formData.append(key, val);
      });
      Object.entries(inspectorAssessment).forEach(([key, val]) => {
        formData.append(key, val);
      });

      // Append reference number
      if (activeRefno) {
        formData.append("refno", activeRefno);
      }
      if (activePt6Date) {
        formData.append("pt6_issue_date", activePt6Date);
      }

      await axios.put(`/properties/assign-supervisor-notfiltered/${selectedProperty.pin}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Property reassessed successfully!", { id: toastId });
      navigate('/inspector/resolved'); // Redirect after successful submission
    } catch (err) {
      console.error("Error submitting reassessment:", err);
      if (openDuplicatePt5Modal(err, async (newPt5) => {
        applyNewPt5(newPt5);
        await handleSubmitReassess({
          featuresOverride: features,
          refnoOverride: newPt5,
          pt6IssueDateOverride: pt6IssueDate,
        });
      })) {
        return;
      }
      toast.error(err.response?.data?.error || "Failed to submit reassessment", { id: toastId });
    } finally {
      setReassessSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchProperty = async () => {
      if (!pin || !userId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await axios.get(`/inspector/algorithm-flagged/${userId}?pin=${pin}`);
        if (res.data && res.data.length > 0) {
          setSelectedProperty(res.data[0]);
          console.log("Fetched selectedProperty:", res.data[0]);
        } else {
          toast.error("Property not found.");
          navigate('/inspector/flaggedbyalgorithm');
        }
      } catch (err) {
        console.error("Error fetching property for reassessment:", err);
        toast.error("Failed to load property details.");
        navigate('/inspector/flaggedbyalgorithm');
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [pin, userId, navigate]);

  useEffect(() => {
    if (selectedProperty) {
      setOverallExplanation(selectedProperty.overall_explanation || "");
      setRowExplanations({
        floor: selectedProperty.floor_explanation || "",
        landuse: selectedProperty.landuse_explanation || "",
        landarea: selectedProperty.landarea_explanation || "",
        coveredarea: selectedProperty.coveredarea_explanation || "",
      });
      setFeatures(buildFeatures(selectedProperty));
      setReferenceNumber(getPt5Reference(selectedProperty));
      setPt6IssueDate("");
    }
  }, [selectedProperty, isCurrentlyReassessed]); // isCurrentlyReassessed depends on selectedProperty, so it's fine

  if (loading) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600">Loading property for reassessment...</p>
      </div>
    );
  }

  if (!selectedProperty) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 items-center justify-center">
        <p className="text-gray-600">No property data available.</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-white flex justify-center items-center z-50 p-4" // Changed background to white for full page
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white shadow-2xl flex flex-col w-full h-full relative" // Removed max-w-4xl and rounded-lg
      >
        <div className="flex-shrink-0 p-6 border-b border-gray-200 pr-14">
          <button
            onClick={() => navigate(-1)} // Navigate back
            className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors z-10 flex items-center justify-center w-10 h-10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h3 className="text-2xl font-bold text-gray-800">Reassessment</h3>
          <p className="text-gray-600 mt-1">PIN: {selectedProperty.pin}</p>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Feature
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current (Admin)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Computer Vision Predicted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Explanation
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {features.map((feature, index) => {
                  const isArea = feature.key === "landarea" || feature.key === "coveredarea";
                  const isLanduse = feature.key === "landuse";
                  return (
                    <tr key={feature.key} className={feature.isDifferent ? "bg-red-50" : ""}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {feature.label}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {isArea ? formatTwoDecimals(feature.admin) : feature.admin || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {feature.ml || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {isLanduse ? (
                          <select
                            value={feature.after || ""}
                            onChange={(e) => updateFeature(index, "after", e.target.value)}
                            className="block w-full px-3 py-2 h-10 rounded-md shadow-sm sm:text-sm border-gray-300 focus:ring-purple-500 focus:border-purple-500"
                          >
                            <option value="">Select Land Use</option>
                            <option value="Residential">Residential</option>
                            <option value="Mixed">Mixed</option>
                            <option value="Commercial">Commercial</option>
                          </select>
                        ) : isArea ? (
                          <div>&nbsp;</div>
                        ) : (
                          <input
                            type="text"
                            placeholder={`Enter ${feature.label.toLowerCase()}...`}
                            className="block w-full px-3 py-2 h-10 rounded-md shadow-sm sm:text-sm border-gray-300 focus:ring-purple-500 focus:border-purple-500"
                            value={feature.after || ""}
                            onChange={(e) => updateFeature(index, "after", e.target.value)}
                          />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {isArea ? (
                          <div>&nbsp;</div>
                        ) : (
                          <input
                            type="text"
                            placeholder={`Explain ${feature.label.toLowerCase()} change...`}
                            className="block w-full px-3 py-2 h-10 rounded-md shadow-sm sm:text-sm border-gray-300 focus:ring-purple-500 focus:border-purple-500"
                            value={rowExplanations[feature.key] || ""}
                            onChange={(e) => updateRowExplanation(feature.key, e.target.value)}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mb-6">
            <label className="block font-medium text-gray-700 mb-2">
              PT-5 Serial Number <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter PT-5 Serial Number..."
            />
          </div>

          <div>
            <label htmlFor="overall-explanation" className="block text-sm font-medium text-gray-700 mb-2">
              Overall Explanation
            </label>
            <textarea
              id="overall-explanation"
              rows="3"
              placeholder="Provide an overall explanation for the reassessment..."
              className="block w-full px-3 py-2 min-h-[7.5rem] border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={overallExplanation}
              onChange={(e) => setOverallExplanation(e.target.value)}
            ></textarea>
          </div>
        </div>

        <div className="flex-shrink-0 flex justify-end gap-4 p-6 pt-4 border-t border-gray-200">
          <button
            onClick={() => navigate(-1)} // Navigate back
            className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (!validateReassessForm()) return;
              setShowRefModal(true);
            }}
            aria-disabled={!isReassessValid || reassessSubmitting}
            disabled={reassessSubmitting}
            className={`px-6 py-2 rounded-lg transition-colors text-sm font-medium text-white ${isReassessValid
              ? "bg-green-500 hover:bg-green-600"
              : "bg-green-300 cursor-not-allowed"
              }`}
          >
            {reassessSubmitting ? "Submitting..." : "Next"}
          </button>
        </div>

        {/* Reference Modal */}
        <AnimatePresence>
          {showRefModal && selectedProperty && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            >
              <motion.div initial={{ scale: 0.85 }} animate={{ scale: 1 }} exit={{ scale: 0.85 }}
                className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl"
              >
                {/* <h2 className="text-xl font-bold mb-4">
                  {selectedProperty?.refno || selectedProperty?.pt5 || selectedProperty?.pt_5 || selectedProperty?.pt
                    ? "Edit PT-5 Serial Number"
                    : "Enter PT-5 Serial Number"}
                </h2> */}

                <h2 className="text-xl font-bold mb-4 text-gray-800">
                  {pt6IssueDate ? "Edit PT-6 Issue Date" : "Enter PT-6 Issue Date"}
                </h2>

                {/* <input
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder="PT-5 Serial Number (Mandatory)"
                  className="w-full p-3 border rounded mb-2"
                  required
                /> */}

                {referenceNumber && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">PT-5 Serial:</span> {referenceNumber}
                    </p>
                  </div>
                )}

                <label className="block font-medium text-gray-700 mb-2">
                  PT-6 Issue Date <span className="text-gray-400">*</span>
                </label>
                <input
                  type="date"
                  value={pt6IssueDate}
                  onChange={(e) => setPt6IssueDate(e.target.value)}
                  className="w-full p-3 border rounded mb-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="text-sm text-gray-500 mb-4">
                  Date when PT-6 was issued
                </p>

                <div className="flex gap-3 mt-4">
                  <button
                    onClick={async () => {
                      if (!pt6IssueDate) {
                        toast.error("PT-6 Issue Date is mandatory.");
                        return;
                      }

                      await trackAction(
                        "pt6_save",
                        selectedProperty?.pin,
                        null,
                        {
                          comment_text: `PT-6 Issue Date set to "${pt6IssueDate}"`
                        }
                      );

                      setShowRefModal(false);
                      handleSubmitReassess();
                    }}
                    disabled={reassessSubmitting}
                    className="flex-1 px-4 py-2 rounded text-white bg-blue-600 hover:bg-blue-700"
                    data-click-id="pt6_save"
                    data-property-id={selectedProperty?.pin}
                  >
                    {reassessSubmitting ? "Saving..." : (selectedProperty?.refno || selectedProperty?.pt5 || selectedProperty?.pt_5 || selectedProperty?.pt ? "Update" : "Save")}
                  </button>
                  <button
                    onClick={() => setShowRefModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                    data-click-id="pt6_cancel"
                    data-property-id={selectedProperty?.pin}
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <DuplicatePt5Modal
          isOpen={showDuplicatePt5Modal}
          onClose={closeDuplicatePt5Modal}
          onSave={saveDuplicatePt5Retry}
          submitting={reassessSubmitting}
        />
      </motion.div>
    </motion.div>
  );
};

export default ReassessmentFormPage;
